import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ChevronRight, TrendingUp } from 'lucide-react';
import {
	ComposedChart,
	Bar,
	Line,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	CartesianGrid
} from 'recharts';
import { motion } from 'framer-motion';

export interface MonthlyContributionData {
	month: string;
	empresa: number;
	propia: number;
	valorTotal?: number;
	extras?: number;
	projected?: boolean;
}

export interface ContributionsEvolutionChartV2Props {
	data: MonthlyContributionData[];
	year: number;
	cumulative?: boolean;
	availableYears?: number[];
	onYearChange?: (year: number) => void;
	onViewDetail?: () => void;
	translate?: (key: string) => string;
	/**
	 * Oculta la serie y la leyenda de "empresa". Para vistas sin aportación de
	 * empresa (individual / autónomo), donde siempre sería 0.
	 */
	hideCompany?: boolean;
	/** Color de la barra/leyenda de "mis aportaciones". Default rosa secundario (B2B). */
	contributionColor?: string;
	/**
	 * Fuerza el empty-state (mensaje "aún no hay aportaciones") en lugar de la gráfica,
	 * aunque `data` traiga puntos. Útil cuando los datos del endpoint NO están scopeados
	 * por plan y no representan a ESTE plan (usuarios con varios planes Arca). Ver el guard
	 * `planHasOwnContributions` en IndividualDashboardV2.
	 */
	emptyState?: boolean;
}

interface TooltipPayload {
	value: number;
	color: string;
	dataKey: string;
	name: string;
	payload: { projected?: boolean };
}

const ContributionsEvolutionChartV2: React.FC<ContributionsEvolutionChartV2Props> = ({
	data,
	year,
	cumulative = false,
	availableYears,
	onYearChange,
	onViewDetail,
	translate: t,
	hideCompany = false,
	contributionColor,
	emptyState = false
}) => {
	const translate = t || ((key: string) => key);
	const propiaFill = contributionColor ?? '#F73469';
	const isEmpty = emptyState || data.length === 0;

	const TOOLTIP_LABELS: Record<string, string> = {
		empresaReal: translate('benefit.chart.company'),
		propiaReal: translate('benefit.chart.myContributions'),
		extrasReal: translate('benefit.chart.transfersBonus'),
		empresaProj: translate('benefit.chart.companyEstimated'),
		propiaProj: translate('benefit.chart.myContributionsEstimated'),
		extrasProj: translate('benefit.chart.transfersEstimated'),
		valorTotalReal: translate('benefit.chart.totalValue'),
		valorTotalProj: translate('benefit.chart.totalValueEstimated')
	};

	const CustomTooltip = ({
		active,
		payload,
		label
	}: {
		active?: boolean;
		payload?: TooltipPayload[];
		label?: string;
	}) => {
		if (!active || !payload?.length) return null;
		const isProjected = payload[0]?.payload?.projected;
		const visible = payload.filter((e) => typeof e.value === 'number' && e.value > 0);
		if (!visible.length) return null;
		return (
			<div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg text-sm">
				<p className="font-semibold mb-1">
					{label}
					{isProjected && (
						<span className="ml-1.5 text-[10px] font-normal text-gray-500 italic">
							({translate('benefit.chart.estimated').toLowerCase()})
						</span>
					)}
				</p>
				{visible.map((e, i) => (
					<p key={i} className="text-gray-500 text-xs">
						<span
							className="inline-block w-2 h-2 rounded-full mr-1.5"
							style={{ backgroundColor: e.color }}
						/>
						{TOOLTIP_LABELS[e.dataKey] || e.name}:{' '}
						<span className="font-medium text-gray-900">
							{e.value.toLocaleString('es-ES', {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2
							})}
							€
						</span>
					</p>
				))}
			</div>
		);
	};

	const rawChartData = cumulative
		? data.reduce<(MonthlyContributionData & { valorTotal?: number })[]>((acc, item) => {
				const prev = acc.length > 0 ? acc[acc.length - 1] : { empresa: 0, propia: 0, extras: 0 };
				const cumEmpresa = prev.empresa + item.empresa;
				const cumPropia = prev.propia + item.propia;
				const cumExtras = (prev.extras || 0) + (item.extras || 0);
				acc.push({
					month: item.month,
					empresa: cumEmpresa,
					propia: cumPropia,
					extras: cumExtras,
					valorTotal: item.valorTotal,
					projected: item.projected
				});
				return acc;
			}, [])
		: data;

	const firstProjectedIdx = rawChartData.findIndex((d) => d.projected);
	const chartData = rawChartData.map((d, i) => {
		const isLastReal = firstProjectedIdx > 0 && i === firstProjectedIdx - 1;
		return {
			...d,
			empresaReal: d.projected ? 0 : d.empresa,
			propiaReal: d.projected ? 0 : d.propia,
			extrasReal: d.projected ? 0 : d.extras || 0,
			empresaProj: d.projected ? d.empresa : 0,
			propiaProj: d.projected ? d.propia : 0,
			extrasProj: d.projected ? d.extras || 0 : 0,
			valorTotalReal: d.projected ? undefined : d.valorTotal,
			valorTotalProj: d.projected ? d.valorTotal : isLastReal ? d.valorTotal : undefined
		};
	});

	const hasValorTotal = chartData.some((d) => d.valorTotal != null);
	const hasExtras = chartData.some((d) => (d.extras || 0) > 0);
	const hasProjected = chartData.some((d) => d.projected);

	return (
		<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
			<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
				<div className="flex items-center gap-2 sm:gap-3">
					<h2 className="text-base sm:text-lg font-semibold">
						{translate('benefit.chart.evolution')} {year}
					</h2>
					{!isEmpty && availableYears && availableYears.length > 1 && onYearChange && (
						<div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
							{availableYears.map((y) => (
								<button
									key={y}
									onClick={() => onYearChange(y)}
									className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
										y === year
											? 'bg-primary text-white shadow-xs'
											: 'text-gray-500 hover:text-gray-900'
									}`}
								>
									{y}
								</button>
							))}
						</div>
					)}
				</div>
				{!isEmpty && (
					<Button variant="ghost" size="sm" className="text-gray-500 gap-1" onClick={onViewDetail}>
						{translate('benefit.chart.viewDetail')} <ChevronRight className="w-4 h-4" />
					</Button>
				)}
			</div>
			<Card className="border-none shadow-xs">
				<CardContent className="p-4 sm:p-6 pt-3 sm:pt-4">
					{isEmpty ? (
						<div className="h-[220px] sm:h-[280px] min-h-[200px] w-full flex flex-col items-center justify-center text-center px-4">
							<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
								<TrendingUp className="w-6 h-6 text-primary" />
							</div>
							<p className="text-sm sm:text-base font-medium text-foreground">
								{translate('benefit.chart.emptyTitle')}
							</p>
							<p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-xs">
								{translate('benefit.chart.emptyDescription')}
							</p>
						</div>
					) : (
						<>
							<div className="h-[220px] sm:h-[280px] min-h-[200px] w-full min-w-[200px]">
								<ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
									<ComposedChart data={chartData} barGap={2}>
										<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
										<XAxis
											dataKey="month"
											tick={{ fontSize: 12, fill: '#9ca3af' }}
											axisLine={false}
											tickLine={false}
										/>
										<YAxis
											tick={{ fontSize: 12, fill: '#9ca3af' }}
											axisLine={false}
											tickLine={false}
											tickFormatter={(v) => `${v}€`}
										/>
										<RechartsTooltip
											content={<CustomTooltip />}
											cursor={{ fill: '#f3f4f6', radius: 4 }}
										/>
										{/* Real bars */}
										{!hideCompany && (
											<Bar
												dataKey="empresaReal"
												name={translate('benefit.chart.company')}
												fill="#0D8044"
												stackId="a"
												radius={[0, 0, 0, 0]}
											/>
										)}
										<Bar
											dataKey="propiaReal"
											name={translate('benefit.chart.myContributions')}
											fill={propiaFill}
											stackId="a"
											radius={hasExtras ? [0, 0, 0, 0] : [4, 4, 0, 0]}
										/>
										{hasExtras && (
											<Bar
												dataKey="extrasReal"
												name={translate('benefit.chart.transfersBonus')}
												fill="rgba(13, 128, 68, 0.25)"
												stackId="a"
												radius={[4, 4, 0, 0]}
											/>
										)}
										{/* Projected bars */}
										{!hideCompany && (
											<Bar
												dataKey="empresaProj"
												name={translate('benefit.chart.companyProjected')}
												fill="rgba(13, 128, 68, 0.3)"
												stackId="a"
												radius={[0, 0, 0, 0]}
											/>
										)}
										<Bar
											dataKey="propiaProj"
											name={translate('benefit.chart.contributionProjected')}
											fill="rgba(247, 52, 105, 0.3)"
											stackId="a"
											radius={[4, 4, 0, 0]}
										/>
										{/* Real value line */}
										{hasValorTotal && (
											<Line
												type="monotone"
												dataKey="valorTotalReal"
												name={translate('benefit.chart.totalValueWithReturn')}
												stroke="#2AE986"
												strokeWidth={2.5}
												dot={{ r: 4, fill: '#2AE986', strokeWidth: 2, stroke: '#fff' }}
												activeDot={{ r: 6, fill: '#2AE986', strokeWidth: 2, stroke: '#fff' }}
												connectNulls={false}
											/>
										)}
										{/* Projected value line */}
										{hasValorTotal && hasProjected && (
											<Line
												type="monotone"
												dataKey="valorTotalProj"
												name={translate('benefit.chart.totalValueEstimated')}
												stroke="#2AE986"
												strokeWidth={2}
												strokeDasharray="6 4"
												strokeOpacity={0.5}
												dot={{
													r: 3,
													fill: '#2AE986',
													fillOpacity: 0.5,
													strokeWidth: 1.5,
													stroke: '#fff'
												}}
												activeDot={{
													r: 5,
													fill: '#2AE986',
													fillOpacity: 0.6,
													strokeWidth: 2,
													stroke: '#fff'
												}}
												connectNulls={false}
											/>
										)}
									</ComposedChart>
								</ResponsiveContainer>
							</div>

							<div className="flex items-center justify-center gap-4 sm:gap-6 mt-3 flex-wrap text-[11px] sm:text-xs">
								{!hideCompany && (
									<div className="flex items-center gap-1.5 text-gray-500">
										<div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm bg-primary" />{' '}
										{translate('benefit.chart.company')}
									</div>
								)}
								<div className="flex items-center gap-1.5 text-gray-500">
									<div
										className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-sm ${
											contributionColor ? '' : 'bg-secondary'
										}`}
										style={contributionColor ? { backgroundColor: contributionColor } : undefined}
									/>{' '}
									{translate('benefit.chart.myContributions')}
								</div>
								{hasExtras && (
									<div className="flex items-center gap-2 text-xs text-gray-500">
										<div className="w-3 h-3 rounded-sm bg-primary/25" />{' '}
										{translate('benefit.chart.transfersBonus')}
									</div>
								)}
								{hasValorTotal && (
									<div className="flex items-center gap-2 text-xs text-gray-500">
										<div
											className="rounded-full"
											style={{ width: 12, height: 3, backgroundColor: '#2AE986' }}
										/>{' '}
										{translate('benefit.chart.totalValue')}
									</div>
								)}
								{hasProjected && (
									<div className="flex items-center gap-2 text-xs text-gray-500">
										<div
											className="rounded-full"
											style={{
												width: 12,
												height: 3,
												backgroundColor: '#2AE986',
												opacity: 0.4
											}}
										/>{' '}
										{translate('benefit.chart.estimated')}
									</div>
								)}
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</motion.div>
	);
};

export default ContributionsEvolutionChartV2;
